import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Payout detail-drawer footer (AUT-128). Two conditional buttons + audit
 * note. Both actions are role + permission + audit gated server-side —
 * this is presentation only.
 *
 * `canRetry` / `canMarkManual` come STRAIGHT from the server-composed
 * `retryEligible` / `markManualEligible` flags (architect guardrail #3 —
 * NEVER recompute the predicate client-side). The backend stays the 422 /
 * 409 authority on the actual call; these flags only decide button
 * visibility. The audit note collapses to the right.
 */
export interface PayoutDetailFooterProps {
  canRetry: boolean;
  canMarkManual: boolean;
  busy: boolean;
  onRetry: () => void;
  onMarkManual: () => void;
}

export const PayoutDetailFooter = ({
  canRetry,
  canMarkManual,
  busy,
  onRetry,
  onMarkManual,
}: PayoutDetailFooterProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: '14px 24px',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {canRetry ? (
        <Button
          variant="contained"
          color="primary"
          disabled={busy}
          onClick={onRetry}
          startIcon={<RefreshIcon />}
        >
          {t('payouts.actions.retry')}
        </Button>
      ) : null}
      {canMarkManual ? (
        <Button
          variant="outlined"
          color="inherit"
          disabled={busy}
          onClick={onMarkManual}
          startIcon={<CheckIcon />}
        >
          {t('payouts.actions.markManual')}
        </Button>
      ) : null}
      <Box
        sx={{
          ml: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 11,
          color: tokens.text.tertiary,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
        {t('payouts.actions.auditNote')}
      </Box>
    </Box>
  );
};
